import { ChangeEvent, Component } from "react";
import ReactCrop, { Crop } from "react-image-crop";
import Button from "components/ui/Button";
import { GhostButton } from "components/adminUi/Button";
import Dialog from "components/ui/Dialog";
import { Box } from "@mui/material";
import ApiErrorBanner from "components/ApiErrorBanner";

// What the cropper hands back: the cropped file and a data URL to preview it.
export type CroppedImage = { file: File; preview: string };

interface ImageCropperDialogProps {
  aspect: number;
  showDialog: boolean;
  onClose: () => void;
  onSubmitPreview: (args: CroppedImage) => void;
  onFileChange: (event: ChangeEvent<HTMLInputElement>) => void;
  file: File | null;
  selectedFileData?: string | null;
  type?: string | null;
}

interface ImageCropperDialogState {
  selectedFile: File | null;
  selectedFileData: string | null;
  preview: string | null;
  showSpinner: boolean;
  errors: unknown;
}

export class ImageCropperDialog extends Component<
  ImageCropperDialogProps,
  ImageCropperDialogState
> {
  state: ImageCropperDialogState = {
    selectedFile: null,
    selectedFileData: null,
    preview: null,
    showSpinner: false,
    errors: "",
  };
  cropper: Cropper | null = null;
  uploadFile = () => {
    if (this.props.file === null) {
      this.setState({ errors: "You must select a file." });
      return;
    }
    const croppedFile = this.cropper?.getCroppedBlob();
    const blobFile = croppedFile ?? this.props.file;
    const file = new File([blobFile], this.props.file.name, {
      type: this.props.type === "svg" ? "image/svg+xml" : "image/jpg",
    });

    this.props.onSubmitPreview({
      file: file,
      preview: this.state.preview!,
    });
  };

  updatePreview = (preview: string) => {
    this.setState({ preview });
  };

  handleErrors = (errors: unknown) => {
    this.setState({ errors: errors });
  };

  render() {
    return (
      <Dialog
        className="image-cropper-dialog"
        size="lg"
        open={this.props.showDialog}
        onClose={this.props.onClose}
        closeOnOverlayClick={false}
        title="Crop Image"
        secondaryAction={
          <GhostButton
            className="cancel"
            onClick={this.props.onClose}
            disabled={this.state.showSpinner}
          >
            Cancel
          </GhostButton>
        }
        mainAction={
          <Button
            size="small"
            variant="primary"
            className="submit"
            onClick={this.uploadFile}
            disabled={this.state.showSpinner}
          >
            Save
          </Button>
        }
        loading={this.state.showSpinner}
      >
        <ApiErrorBanner error={this.state.errors} />
        <input
          type="file"
          accept="image/jpg, image/jpeg, image/png, image/svg+xml"
          onChange={this.props.onFileChange}
        />
        {this.props.file !== null &&
          this.props.selectedFileData &&
          this.props.type !== "svg" && (
            <div style={{ minWidth: 500 }}>
              <Cropper
                src={this.props.selectedFileData}
                filename={this.props.file.name}
                updatePreview={this.updatePreview}
                handleErrors={this.handleErrors}
                aspect={this.props.aspect}
                ref={c => (this.cropper = c)}
              />
            </div>
          )}
      </Dialog>
    );
  }
}

interface CropperProps {
  src: string;
  updatePreview: (preview: string) => void;
  handleErrors: (errors: unknown) => void;
  filename: string;
  aspect: number;
}

interface CropperState {
  src: string;
  crop: Crop;
  blob: Blob | null;
  croppedImageUrl: string;
}

export class Cropper extends Component<CropperProps, CropperState> {
  state: CropperState = {
    src: this.props.src,
    crop: {
      unit: "%",
      width: 64,
      aspect: this.props.aspect,
    } as Crop,
    blob: null,
    croppedImageUrl: "",
  };
  imageRef: HTMLImageElement | null = null;
  fileUrl: string | null = null;

  componentDidUpdate(prevProps: CropperProps) {
    if (this.props.src !== prevProps.src) {
      this.setState({
        src: this.props.src,
      });
    }
  }

  // If you setState the crop in here you should return false.
  onImageLoaded = (image: HTMLImageElement) => {
    this.imageRef = image;
  };

  onCropComplete = (crop: Crop) => {
    this.makeClientCrop(crop);
  };

  onCropChange = (crop: Crop) => {
    this.setState({ crop: crop });
  };

  makeClientCrop = (crop: Crop) => {
    if (this.imageRef && crop.width && crop.height) {
      this.getCroppedImg(this.imageRef, crop)
        .then(({ fileUrl, blob }: { fileUrl: string; blob: Blob }) => {
          this.setState({ croppedImageUrl: fileUrl, blob: blob });
          this.props.updatePreview(fileUrl);
        })
        .catch((errors: unknown) => {
          this.props.handleErrors(errors);
        });
    }
  };

  getCroppedImg = (image: HTMLImageElement, crop: Crop) => {
    return new Promise<{ fileUrl: string; blob: Blob }>((resolve, reject) => {
      if (
        /* eslint-disable @typescript-eslint/no-unnecessary-condition --
           react-image-crop types a Crop's fields as set; a fresh one has none. */
        crop?.width === undefined ||
        crop.height === undefined ||
        crop.x === undefined ||
        crop.y === undefined
        /* eslint-enable @typescript-eslint/no-unnecessary-condition */
      ) {
        reject(new Error("Something is wrong, please try again."));
        return;
      }
      let height = 600;
      let width = 600;
      if (this.props.aspect === 1) {
        height = 600;
        width = 600;
      } else if (this.props.aspect === 9 / 16) {
        height = 600;
        width = 400;
      } else if (this.props.aspect === 16 / 9) {
        height = 400;
        width = 600;
      }
      const canvas = document.createElement("canvas");
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;
      const ctx = canvas.getContext("2d");
      if (ctx === null) {
        reject(new Error("Could not get a 2d canvas context."));
        return;
      }

      canvas.width = width;
      canvas.height = height;
      ctx.imageSmoothingQuality = "high";

      ctx.drawImage(
        image,
        crop.x * scaleX,
        crop.y * scaleY,
        crop.width * scaleX,
        crop.height * scaleY,
        0,
        0,
        width,
        height
      );
      canvas.toBlob(
        (blob: Blob | null) => {
          if (blob === null) {
            reject(new Error("Error occurred during cropping, try again."));
            return;
          }
          if (this.fileUrl !== null) {
            window.URL.revokeObjectURL(this.fileUrl);
          }
          // window.URL.revokeObjectURL(previewUrl);
          this.fileUrl = window.URL.createObjectURL(blob);
          resolve({
            fileUrl: this.fileUrl,
            blob: blob,
          });
        },
        "image/jpg",
        1
      );
    });
  };
  getCroppedBlob = () => {
    return this.state.blob;
  };

  render() {
    const { crop, src } = this.state;
    return (
      <>
        <ReactCrop
          src={src}
          crop={crop}
          onImageLoaded={this.onImageLoaded}
          onComplete={this.onCropComplete}
          onChange={this.onCropChange}
        />
        {this.state.croppedImageUrl && (
          <Box
            sx={{
              flexDirection: "column",
              display: "flex",
            }}
          >
            <span>Preview</span>
            <img
              src={this.state.croppedImageUrl}
              alt={"Preview"}
              style={{ width: "30%", height: "30%" }}
            />
          </Box>
        )}
      </>
    );
  }
}
